import { isApiClientError } from "../api/errors";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { hasPermission } from "../entities/access";
import {
  auditActionLabel,
  auditCategoryLabel,
  auditObjectKindLabel,
  auditResultLabel,
  auditSeverityLabel,
  auditSourceLabel,
} from "../entities/audit";
import type { AuditEvent, AuditFilters } from "../entities/audit";
import { shellBootstrapQuery } from "./bootstrap";
import type { ShellServices } from "./services";
import { EmptyState } from "./states";
import { Select } from "./Select";
import { Notice } from "./Notice";

const filterNames = ["from", "to", "actor", "category", "action", "object_type", "object_id", "ip_address", "correlation_id", "result", "severity", "source"] as const;
type FilterName = (typeof filterNames)[number];
type AuditSearch = Partial<Record<FilterName, string>>;

// eslint-disable-next-line react-refresh/only-export-components -- маршрут использует валидатор query string.
export function validateAuditSearch(value: Record<string, unknown>): AuditSearch {
  const result: AuditSearch = {};
  for (const key of filterNames) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim()) result[key] = candidate;
  }
  return result;
}

function filtersFromSearch(search: AuditSearch): AuditFilters {
  const filters: Record<string, string> = {};
  for (const name of filterNames) if (search[name]) filters[name] = search[name]!;
  return filters;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeStyle: "medium" }).format(new Date(value));
}

function safeValue(value: unknown, key = ""): unknown {
  if (/password|token|secret|authorization|cookie|recovery|otp/i.test(key)) return "[скрыто]";
  if (Array.isArray(value)) return value.map((item) => safeValue(item));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([name, item]) => [name, safeValue(item, name)]));
  return value;
}

function Values({ title, value }: { title: string; value: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  // Пустой раздел не показывается вовсе: заголовок «Старые значения» без
  // единого значения под ним только мешает читать запись.
  if (!value || Object.keys(value).length === 0) return null;
  return <section className="audit-values"><button className="button button--text" type="button" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? "Скрыть" : title}</button>{open ? <pre>{JSON.stringify(safeValue(value), null, 2)}</pre> : null}</section>;
}

/** Название объекта события, а если его нет — вид объекта словами. */
function objectTitle(event: AuditEvent): string | null {
  if (!event.object) return null;
  const kind = auditObjectKindLabel(event.object.kind);
  return event.object.label ? `${kind} «${event.object.label}»` : kind;
}

/**
 * Ссылка на сам объект там, где в ERP есть куда вести.
 *
 * Отдельных экранов пользователя и файла в интерфейсе нет — есть разделы, — а
 * документ открывается по идентификатору в адресе. Ведём туда, куда реально
 * можно привести, и не выдумываем адреса строками: маршруты собирает router.
 */
function ObjectLink({ event }: { event: AuditEvent }) {
  const title = objectTitle(event);
  if (!event.object || !title) return <>—</>;
  if (event.object.kind === "document") {
    return <Link to="/modules/document-workflow/documents" search={{ document: event.object.id }}>{title}</Link>;
  }
  if (event.object.kind === "file" || event.object.kind === "media") {
    return <Link to="/administration/files">{title}</Link>;
  }
  if (event.object.kind === "user") {
    return <Link to="/administration/users/list">{title}</Link>;
  }
  if (event.object.kind === "role") {
    return <Link to="/administration/roles">{title}</Link>;
  }
  if (event.object.kind === "scope" || event.object.kind === "department") {
    return <Link to="/administration/scopes">{title}</Link>;
  }
  if (event.object.kind === "module") {
    return <Link to="/administration/modules">{title}</Link>;
  }
  return <>{title}</>;
}

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return <button className="button button--text button--compact" type="button" onClick={() => { void navigator.clipboard?.writeText(value); setCopied(true); }}>{copied ? "Скопировано" : label}</button>;
}

/**
 * Название организации события (ES-100). События видны только из доступных
 * организаций, а их названия уже есть в переключателе организаций; событие
 * без организации — системное.
 */
function scopeLabel(scopeId: string | null, scopes: readonly { id: string; name: string }[]): string {
  if (scopeId === null) return "Вне организации";
  return scopes.find((scope) => scope.id === scopeId)?.name ?? "Организация";
}

function isForbidden(error: unknown): boolean {
  return isApiClientError(error) && error.status === 403;
}

function EventRow({ event, scopeName }: { event: AuditEvent; scopeName: string }) {
  const title = objectTitle(event);
  return (
    <details className="audit-event">
      <summary>
        <time dateTime={event.occurred_at}>{formatDate(event.occurred_at)}</time>
        <span className="audit-event__headline">
          <strong>{auditActionLabel(event.action)}</strong>
          {title ? <span className="audit-event__object">{title}</span> : null}
        </span>
        <span className="audit-event__actor">
          {event.actor ? (
            <Link to="/administration/audit" search={{ actor: event.actor.id }}>{event.actor.display_name}</Link>
          ) : (
            auditSourceLabel(event.source)
          )}
          <span className="dw-secondary">{auditCategoryLabel(event.category)}</span>
          <span className="dw-secondary audit-event__scope">{scopeName}</span>
        </span>
        <span className={`audit-result audit-result--${event.result}`}>{auditResultLabel(event.result)}</span>
      </summary>
      <div className="audit-event__details">
        <dl>
          <div>
            <dt>Объект</dt>
            <dd><ObjectLink event={event} /></dd>
          </div>
          <div>
            <dt>Событие</dt>
            <dd><code>{event.action}</code></dd>
          </div>
          <div>
            <dt>Категория</dt>
            <dd>{auditCategoryLabel(event.category)} <span className="dw-secondary">{event.category}</span></dd>
          </div>
          <div>
            <dt>Источник</dt>
            <dd>{auditSourceLabel(event.source)}</dd>
          </div>
          <div>
            <dt>Значимость</dt>
            <dd>{auditSeverityLabel(event.severity)}</dd>
          </div>
          <div>
            <dt>IP</dt>
            <dd>{event.ip_address ?? "—"}</dd>
          </div>
          <div>
            <dt>Идентификатор объекта</dt>
            <dd>
              {event.object_type && event.object_id ? (
                <>
                  <Link to="/administration/audit" search={{ object_type: event.object_type, object_id: event.object_id }}>
                    Все события объекта
                  </Link>
                  <span className="dw-secondary">{event.object_type} · {event.object_id}</span>
                  <CopyButton label="Скопировать ID" value={event.object_id} />
                </>
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div>
            <dt>Correlation ID</dt>
            <dd>
              {event.correlation_id ? (
                <>
                  <span className="dw-secondary">{event.correlation_id}</span>
                  <CopyButton label="Скопировать" value={event.correlation_id} />
                </>
              ) : (
                "—"
              )}
            </dd>
          </div>
        </dl>
        <div className="audit-value-grid">
          <Values title="Старые значения" value={event.old_values} />
          <Values title="Новые значения" value={event.new_values} />
        </div>
      </div>
    </details>
  );
}

/** Дата и время выгрузки в привычном виде. */
function exportDate(value: string | null): string {
  return value === null
    ? "—"
    : new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

const exportStates: Record<string, string> = {
  pending: "в очереди",
  processing: "формируется",
  ready: "готова",
  failed: "ошибка формирования",
  expired: "срок хранения истёк",
};

function ExportPanel({ services, filters, allowed }: { services: ShellServices; filters: AuditFilters; allowed: boolean }) {
  const client = useQueryClient();
  const [exportId, setExportId] = useState<string | null>(null);
  const create = useMutation({ mutationFn: () => services.audit.requestExport(filters), onSuccess: async (item) => { setExportId(item.id); await client.invalidateQueries({ queryKey: ["audit", "exports"] }); } });
  const status = useQuery({ queryKey: ["audit", "export", exportId], queryFn: () => services.audit.exportStatus(exportId!), enabled: exportId !== null, refetchInterval: (query) => query.state.data?.status === "pending" || query.state.data?.status === "processing" ? 1500 : false });
  const download = useMutation({ mutationFn: async () => { const bytes = await services.audit.download(exportId!); const url = URL.createObjectURL(new Blob([bytes], { type: "text/csv" })); const link = document.createElement("a"); link.href = url; link.download = "audit-export.csv"; link.click(); URL.revokeObjectURL(url); } });
  // Заказанные выгрузки живут на сервере: список нужен, чтобы найти их после
  // перезагрузки страницы, а не только там, где нажали кнопку.
  const history = useQuery({
    queryKey: ["audit", "exports"],
    queryFn: () => services.audit.exports(),
    enabled: allowed,
    refetchInterval: (query) =>
      query.state.data?.some((item) => item.status === "pending" || item.status === "processing")
        ? 3000
        : false,
    retry: false,
  });
  const saveFile = async (id: string) => {
    const bytes = await services.audit.download(id);
    const url = URL.createObjectURL(new Blob([bytes], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-export-${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const state = status.data?.status;
  const label = state === "pending" ? "queued — в очереди" : state === "processing" ? "processing — формируется" : state === "ready" ? "completed — готов" : state === "failed" ? "failed — ошибка формирования" : null;
  if (!allowed) return <p className="audit-export__restricted">Экспорт скрыт: у вашей роли нет права <code>audit.export</code>.</p>;
  return <aside className="audit-export" aria-label="Экспорт аудита"><h2>Экспорт</h2><p>CSV включает события с текущими фильтрами и только тех организаций, к которым у вас есть доступ.</p><button className="button button--primary" type="button" disabled={create.isPending || state === "pending" || state === "processing"} onClick={() => create.mutate()}>{create.isPending ? "Создаём…" : "Создать CSV"}</button>{create.isError ? <Notice>{isForbidden(create.error) ? "У вас нет права на экспорт аудита ни в одной из доступных организаций" : "Не удалось создать экспорт."}</Notice> : null}{label ? <Notice role="status">Статус: <strong>{label}</strong>{status.data?.error_code ? ` (${status.data.error_code})` : ""}</Notice> : null}{state === "ready" ? <button className="button button--secondary" type="button" disabled={download.isPending} onClick={() => download.mutate()}>{download.isPending ? "Скачиваем…" : "Скачать CSV"}</button> : null}{status.isError ? <Notice>Не удалось проверить статус экспорта.</Notice> : null}
    <h3>Заказанные выгрузки</h3>
    {history.isError ? (
      <Notice>Не удалось загрузить список выгрузок.</Notice>
    ) : history.data?.length ? (
      <ul className="audit-export__list">
        {history.data.map((item) => (
          <li key={item.id}>
            <span>
              <strong>{exportStates[item.status] ?? item.status}</strong>
              <small>
                {exportDate(item.created_at)}
                {item.size_bytes === null ? "" : ` · ${Math.max(1, Math.round(item.size_bytes / 1024))} КБ`}
              </small>
            </span>
            {item.status === "ready" ? (
              <button className="button button--text button--compact" type="button" onClick={() => void saveFile(item.id)}>
                Скачать
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    ) : (
      <p className="dw-secondary">Выгрузок пока нет.</p>
    )}
  </aside>;
}

export function AuditLogPage({ services }: { services: ShellServices }) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- router search inference is untyped here.
  const search = useSearch({ from: "/authenticated/administration/audit" }) as AuditSearch;
  const navigate = useNavigate({ from: "/administration/audit" });
  const filters = filtersFromSearch(search);
  const bootstrap = useQuery(shellBootstrapQuery(services));
  // Курсор живёт только внутри кэша запроса, а не в адресной строке: ссылку на
  // журнал шлют коллеге ради фильтров, а не ради «страницы», на которой человек
  // остановился. Смена фильтров меняет queryKey — список начинается заново.
  const events = useInfiniteQuery({ queryKey: ["audit", "events", search], queryFn: ({ pageParam }) => services.audit.list({ ...filters, cursor: pageParam }), initialPageParam: undefined as string | undefined, getNextPageParam: (page) => page.meta.next_cursor ?? undefined });
  const loadedEvents = events.data?.pages.flatMap((page) => page.data) ?? [];
  const advancedFilterCount = ["actor", "object_type", "object_id", "ip_address", "correlation_id", "severity"].filter((name) => search[name as FilterName]).length;
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(advancedFilterCount > 0);
  const setSearch = (next: AuditSearch) => void navigate({ search: next });
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const form = new FormData(event.currentTarget); const next: AuditSearch = {}; for (const name of filterNames) { const candidate = form.get(name); const raw = typeof candidate === "string" ? candidate.trim() : ""; const value = (name === "from" || name === "to") && raw ? new Date(raw).toISOString() : raw; if (value) next[name] = value; } setSearch(next); };
  const toggleQuickFilter = (name: Extract<FilterName, "result" | "severity" | "source">, value: string) => setSearch({ ...search, [name]: search[name] === value ? undefined : value });
  // Весь холдинг видят системный администратор и обладатель роли без
  // организации (генеральный директор) — ES-88, ES-100.
  const seesWholeHolding = bootstrap.data?.user.is_system_administrator === true || (bootstrap.data?.user.holding_wide_roles ?? []).length > 0;
  const canExport = bootstrap.data ? hasPermission({ permissions: bootstrap.data.permissions }, "audit.export") : false;
  return <section className="workspace-page audit-page" aria-labelledby="audit-title"><div className="page-heading"><p className="eyebrow">Контроль и соответствие</p><h1 id="audit-title">Журнал аудита</h1><p>Неизменяемая история критичных действий. Это не технические application logs.</p>{bootstrap.data?.allOrganizations ? <p className="audit-mode-hint" role="note">{seesWholeHolding ? "Показаны события всех организаций." : "Показаны события организаций, к которым у вас есть доступ."}</p> : null}</div><form key={JSON.stringify(search)} className="dw-panel audit-filters" onSubmit={submit}><div className="panel-heading dw-filter-heading"><div><h2>Фильтры</h2><p>Поиск применяется на сервере и сохраняется в адресной строке.</p></div><div className="dw-filter-actions"><button className="button button--text" type="button" aria-expanded={showAdvancedFilters} aria-controls="audit-advanced-filters" onClick={() => setShowAdvancedFilters((value) => !value)}>{showAdvancedFilters ? "Скрыть расширенные" : "Расширенные"}{advancedFilterCount ? <span className="dw-action-count">{advancedFilterCount}</span> : null}</button><button className="button button--text" type="button" onClick={() => setSearch({})}>Сбросить</button></div></div><div className="dw-quick-filters" aria-label="Быстрые фильтры"><button type="button" className={search.result === "failure" ? "dw-chip dw-chip--on" : "dw-chip"} onClick={() => toggleQuickFilter("result", "failure")}>Ошибки</button><button type="button" className={search.result === "denied" ? "dw-chip dw-chip--on" : "dw-chip"} onClick={() => toggleQuickFilter("result", "denied")}>Отказы</button><button type="button" className={search.severity === "critical" ? "dw-chip dw-chip--on" : "dw-chip"} onClick={() => toggleQuickFilter("severity", "critical")}>Критичные</button><button type="button" className={search.source === "user" ? "dw-chip dw-chip--on" : "dw-chip"} onClick={() => toggleQuickFilter("source", "user")}>Действия пользователей</button></div><div className="dw-filter-grid dw-filter-grid--primary"><label className="field"><span>Действие</span><input name="action" defaultValue={search.action} placeholder="Например, user.updated"/></label><label className="field"><span>Категория</span><Select name="category" defaultValue={search.category ?? ""}><option value="">Все</option>{["authentication","authorization","user_management","role_management","configuration","security","file_access","module_management","system"].map((value) => <option key={value}>{value}</option>)}</Select></label><label className="field"><span>Результат</span><Select name="result" defaultValue={search.result ?? ""}><option value="">Все</option><option value="success">Успех</option><option value="failure">Ошибка</option><option value="denied">Отказ</option></Select></label></div>{showAdvancedFilters ? <div className="dw-advanced-filters" id="audit-advanced-filters"><div className="dw-filter-grid"><label className="field"><span>С</span><input name="from" type="datetime-local" defaultValue={search.from?.slice(0, 16)}/></label><label className="field"><span>По</span><input name="to" type="datetime-local" defaultValue={search.to?.slice(0, 16)}/></label><label className="field"><span>Actor UUID</span><input name="actor" defaultValue={search.actor}/></label><label className="field"><span>Тип объекта</span><input name="object_type" defaultValue={search.object_type}/></label><label className="field"><span>ID объекта</span><input name="object_id" defaultValue={search.object_id}/></label><label className="field"><span>IP-адрес</span><input name="ip_address" defaultValue={search.ip_address}/></label><label className="field"><span>Correlation ID</span><input name="correlation_id" defaultValue={search.correlation_id}/></label><label className="field"><span>Severity</span><Select name="severity" defaultValue={search.severity ?? ""}><option value="">Все</option>{["debug","info","warning","error","critical"].map((value) => <option key={value}>{value}</option>)}</Select></label><label className="field"><span>Источник</span><Select name="source" defaultValue={search.source ?? ""}><option value="">Все</option>{["user","system","scheduler","queue","integration","administrator"].map((value) => <option key={value}>{value}</option>)}</Select></label></div></div> : null}<div className="audit-filter-submit"><button className="button button--primary" type="submit">Применить фильтры</button></div></form><ExportPanel services={services} filters={filters} allowed={canExport}/>{events.isPending ? <p aria-busy="true">Загружаем события…</p> : events.isError ? <Notice>{isForbidden(events.error) ? "У вас нет права на просмотр журнала аудита ни в одной из доступных организаций" : "Не удалось загрузить журнал. Повторите попытку."}</Notice> : loadedEvents.length ? <div className="audit-events">{loadedEvents.map((event) => <EventRow key={event.id} event={event} scopeName={scopeLabel(event.scope_id, bootstrap.data?.scopes ?? [])}/>)}</div> : <EmptyState title="Событий не найдено" description="Измените фильтры или расширьте период поиска."/>}{events.hasNextPage ? <div className="audit-more"><button className="button button--secondary" type="button" disabled={events.isFetchingNextPage} onClick={() => void events.fetchNextPage()}>{events.isFetchingNextPage ? "Загружаем…" : "Показать ещё"}</button></div> : null}</section>;
}
